import { ManualInterventionStatus } from '@fruitful/investment-management';
import { MoneyMapCategoryToAccountColorMap } from '@fruitful/money-map';
import {
    AnalyticsUserEvent,
    ApexAccountSectionType,
    apexAccountTypeToSection,
    MoneyMapCategory,
    type MoneyMapNewApexAccount,
    ReadableApexAccountRegistrationType,
} from '@fruitful/types';
import { useFeatureGate } from '@statsig/expo-bindings';
import { type Href, Link, Redirect } from 'expo-router';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
    BodyMediumRegular,
    BodySmallEmphasized,
    Button,
    ButtonSize,
    ButtonVariant,
    FRUITFUL_ICON_KEY,
    FruitfulCustomIcon,
    FullScreenSpinner,
    LabelRegular,
    ScreenContainer,
    Spinner,
    useToastController,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { BatchOpenApexAccountsBankingTerms } from '../../../banking/components/BankingTerms';
import {
    POLICY_DISCLOSURES_BATCH_OPENING,
    PolicyType,
} from '../../../banking/components/Disclosures';
import { useAuthedUser } from '../../../common/providers/AuthedUserProvider';
import { useViewEvents } from '../../../events/hooks/useViewEvents';
import { FeatureFlags } from '../../../feature-flags/FeatureFlags';
import { useGetUserInvestmentProfile } from '../../../investment-profile/hooks/useGetUserInvestmentProfile';
import {
    captureException,
    captureWarning,
    track,
} from '../../../logger/Logger';
import { SUPPORT_EMAIL } from '../../../settings/types/constants';
import { useCreateMoneyMapApexAccounts } from '../../hooks/useCreateMoneyMapApexAccounts';
import { useMoneyMapWorkflow } from '../../hooks/useMoneyMapWorkflow';
import { GoWorkflowStepType } from '../../types/GoWorkflow';
import {
    canProceedToIncomeSplit,
    memberHasNoApexActionsLeft,
    mustWaitForPartner,
} from '../../utils/apexAccountsStepState';
import { GoWorkflowHeader } from '../GoWorkflowHeader';
import {
    type ApexComplianceCompletion,
    ApexComplianceFlow,
} from './accounts/AccountStepCompliance';
import { ApexAccountsSuccess } from './accounts/ApexAccountsSuccess';
import { ApexAccountsUnderReview } from './accounts/ApexAccountsUnderReview';
import { ApexAgreementCheckbox } from './accounts/ApexAgreementCheckbox';

// Section headers
const sectionHeaders: Record<
    ApexAccountSectionType,
    { singular: string; plural: string }
> = {
    [ApexAccountSectionType.INDIVIDUAL_BROKERAGE]: {
        singular: 'Open this individual brokerage account',
        plural: 'Open these individual brokerage accounts',
    },
    [ApexAccountSectionType.RETIREMENT]: {
        singular: 'Open this individual retirement account',
        plural: 'Open these individual retirement accounts',
    },
    [ApexAccountSectionType.JOINT_BROKERAGE]: {
        singular: 'Open this joint brokerage account',
        plural: 'Open these joint brokerage accounts',
    },
};

export const ApexAccounts = () => {
    const { trackViewOnce } = useViewEvents();
    const { value: enableApexAccounts } = useFeatureGate(
        FeatureFlags.ENABLE_IM_TAB,
    );

    const toast = useToastController();
    const [acceptTerms, setAcceptTerms] = useState<boolean>(false);
    const [agreementCheckboxes, setAgreementCheckboxes] = useState<
        Record<string, boolean>
    >({});

    const [isCreatingAccounts, setIsCreatingAccounts] =
        useState<boolean>(false);
    const [showBackgroundSheet, setShowBackgroundSheet] =
        useState<boolean>(false);

    const user = useAuthedUser();

    const {
        isLoadingMoneyMap,
        moneyMap,
        hasMoneyMapError,
        isLoadingCompleteness,
        refetchMoneyMapCompleteness,
        moneyMapCompleteness,
        trackWorkflowCompletedEvent,
    } = useMoneyMapWorkflow();
    const { apexAccountsStep } =
        // use ref because we don't want this and things derived from it to change after step is submitted
        useRef(moneyMapCompleteness).current;
    const {
        isIncluded,
        isEnabled,
        memberSoloAccountsToOpen: soloAccountsToOpen = [],
        memberJointAccountsToAccept: jointAccountsToAccept = [],
        jointAccountsToOpen = [],
        partnerJointAccountsToAccept = [],
        partnerSoloAccountsToOpen = [],
    } = apexAccountsStep ?? {};

    const apexPrivacyNoticeUri =
        POLICY_DISCLOSURES_BATCH_OPENING[PolicyType.APEX_PRIVACY_NOTICE].url;

    const { agreementLinks, allPossibleAgreements } = useMemo(() => {
        // Collect all Apex accounts from the money map accounts to open/accept
        const allApexAccountsToAction = [
            ...soloAccountsToOpen,
            ...jointAccountsToOpen,
            ...jointAccountsToAccept,
        ];

        // Extract unique agreements by agreementUri, storing friendlyName
        const agreementMap = new Map<string, string>();
        // All possible agreements (will be filtered by checked status later)
        const possibleAgreements: Array<{
            moneyMapAccountId: string;
            agreementUri: string;
            checked: boolean;
        }> = [];

        for (const account of allApexAccountsToAction) {
            const agreements = account.derived?.apexAgreements ?? [];
            for (const agreement of agreements) {
                if (
                    agreement.agreementType === 'APEX_PRIVACY_NOTICE' &&
                    agreement.agreementUri !== apexPrivacyNoticeUri
                ) {
                    // TODO what kind of capture will trigger a slack alert?
                    captureWarning(
                        `Encountered agreement with type ${agreement.agreementType} and URI ${agreement.agreementUri} for account ${account.id}. This does not match the expected APEX_PRIVACY_NOTICE pattern. An Apex agreement may have been updated.`,
                    );
                }

                // Build agreementMap for unique agreements
                if (
                    !agreementMap.has(agreement.agreementUri) &&
                    agreement.agreementType !== 'APEX_PRIVACY_NOTICE'
                ) {
                    // Use Map to ensure uniqueness by agreementUri
                    // If multiple accounts have the same URI, we'll use the first friendlyName we encounter
                    agreementMap.set(
                        agreement.agreementUri,
                        agreement.friendlyName,
                    );
                }

                // Build all possible Apex agreements
                possibleAgreements.push({
                    moneyMapAccountId: account.id,
                    agreementUri: agreement.agreementUri,
                    checked: false, // Will be computed based on agreementCheckboxes
                });
            }
        }

        // Build agreementLinks array from unique agreements
        // Use agreementUri directly as the id since it's unique
        const links = Array.from(agreementMap.entries()).map(
            ([agreementUri, friendlyName]) => ({
                id: agreementUri,
                linkText: friendlyName,
                linkUrl: agreementUri as unknown as Href,
                text: "By checking this box you confirm that you have reviewed and accept Apex Clearing's",
            }),
        );

        return {
            agreementLinks: links,
            allPossibleAgreements: possibleAgreements,
        };
    }, [soloAccountsToOpen, jointAccountsToOpen, jointAccountsToAccept]);

    const memberHasAccountsToOpen =
        soloAccountsToOpen.length > 0 || jointAccountsToOpen.length > 0;
    const memberHasAccountsToAccept = jointAccountsToAccept.length > 0;
    const isNextStepIncomeSplit =
        moneyMapCompleteness.incomeSplitStep.isIncluded &&
        !moneyMapCompleteness.incomeSplitStep.isCompleteForMember;

    const { mutate: createApexAccounts } = useCreateMoneyMapApexAccounts();

    const {
        data: investmentProfile,
        isLoading: isLoadingInvestmentProfile,
        refetch: refetchInvestmentProfile,
    } = useGetUserInvestmentProfile({
        userId: user.id,
    });

    const showBackgroundInfoSheet = () => {
        setShowBackgroundSheet(true);
    };

    const buttonText = isNextStepIncomeSplit
        ? 'Accept and continue'
        : 'Accept and submit';

    const submitApexAccountsStep = useCallback(
        ({ ssn, finra3210 }: ApexComplianceCompletion) => {
            track(
                AnalyticsUserEvent.MONEY_MAP_WORKFLOW_STEP_SUBMIT_APEX_ACCOUNTS,
            );

            setIsCreatingAccounts(true);

            // Build agreementMap array from checked agreements
            const agreementMap = allPossibleAgreements
                .filter(
                    (mmaAndUri) =>
                        (agreementCheckboxes[mmaAndUri.agreementUri] ??
                            false) ||
                        mmaAndUri.agreementUri === apexPrivacyNoticeUri, // Apex privacy policy tracked by other checkbox
                )
                .map(({ moneyMapAccountId, agreementUri }) => ({
                    moneyMapAccountId,
                    agreementUri,
                }));

            createApexAccounts(
                {
                    urlParams: {
                        id: moneyMap?.id ?? '',
                    },
                    body: {
                        agreements: {
                            agreementMap,
                        },
                        submittedApexAccounts: {
                            memberSoloAccountsToOpen: soloAccountsToOpen.map(
                                (a) => a.id,
                            ),
                            jointAccountsToOpen: jointAccountsToOpen.map(
                                (a) => a.id,
                            ),
                            memberJointAccountsToAccept:
                                jointAccountsToAccept.map((a) => a.id),
                            partnerJointAccountsToAccept:
                                partnerJointAccountsToAccept.map((a) => a.id),
                            partnerSoloAccountsToOpen:
                                partnerSoloAccountsToOpen.map((a) => a.id),
                        },
                        // Only include SSN if it's required (no existing NLP); use SSN emitted from compliance flow
                        ssn: ssn ?? undefined,
                        finra3210,
                    },
                },
                {
                    onSettled: async (result) => {
                        setIsCreatingAccounts(false);

                        /**
                         * Reload investment profile so manual intervention flag is up to date.
                         * Use the refetch result (not the hook's investmentProfile) because the
                         * component hasn't re-rendered yet, so the hook value is still stale.
                         */
                        const { data: updatedInvestmentProfile } =
                            await refetchInvestmentProfile();

                        if (result?.success === true) {
                            await refetchMoneyMapCompleteness();
                            trackWorkflowCompletedEvent(
                                GoWorkflowStepType.APEX_ACCOUNTS,
                            );
                        } else if (
                            updatedInvestmentProfile?.manualInterventionStatus ===
                            ManualInterventionStatus.REQUIRED
                        ) {
                            /**
                             * No-op to skip toast.
                             * Next render will display UnderReview
                             */
                        } else {
                            toast.show(
                                `There was an error while creating your Money Map accounts. Please reach out to support at ${SUPPORT_EMAIL}`,
                            );
                        }
                    },
                },
            );
        },
        [
            agreementLinks,
            allPossibleAgreements,
            agreementCheckboxes,
            createApexAccounts,
            refetchInvestmentProfile,
            refetchMoneyMapCompleteness,
            moneyMap,
            soloAccountsToOpen,
            jointAccountsToOpen,
            jointAccountsToAccept,
            partnerJointAccountsToAccept,
            partnerSoloAccountsToOpen,
            trackWorkflowCompletedEvent,
            toast,
        ],
    );

    const handleComplianceComplete = useCallback(
        (payload: ApexComplianceCompletion) => {
            setShowBackgroundSheet(false);
            submitApexAccountsStep(payload);
        },
        [submitApexAccountsStep],
    );

    const currentApexAccountsStep = moneyMapCompleteness.apexAccountsStep;
    const apexStep = currentApexAccountsStep ?? undefined;
    const isWaitingForPartner = mustWaitForPartner(apexStep);
    const canProceedToIncomeSplitStep = canProceedToIncomeSplit(apexStep);

    // Group accounts by type
    // Note: This component does NOT use AccountsSection from banking/components/add-card
    // because APEX accounts require different grouping logic:
    // - Groups by account registration type (Individual Brokerage, Retirement, Joint Brokerage)
    // - Each section embeds its specific agreement checkbox
    // AccountsSection groups by ownership status (solo/joint) and action (open/accept)
    // which doesn't match the requirements for investment account display.
    const accountsByType = useMemo(() => {
        const allAccounts = [
            ...soloAccountsToOpen,
            ...jointAccountsToOpen,
            ...jointAccountsToAccept,
        ] as MoneyMapNewApexAccount[];

        const individualBrokerage: MoneyMapNewApexAccount[] = [];
        const retirement: MoneyMapNewApexAccount[] = [];
        const jointBrokerage: MoneyMapNewApexAccount[] = [];

        allAccounts.forEach((account) => {
            const accountType = account.createParameters?.apexAccountType;
            if (accountType == null) return;

            const sectionType = apexAccountTypeToSection[accountType];

            switch (sectionType) {
                case ApexAccountSectionType.INDIVIDUAL_BROKERAGE:
                    individualBrokerage.push(account);
                    break;
                case ApexAccountSectionType.RETIREMENT:
                    retirement.push(account);
                    break;
                case ApexAccountSectionType.JOINT_BROKERAGE:
                    jointBrokerage.push(account);
                    break;
            }
        });

        return { individualBrokerage, retirement, jointBrokerage };
    }, [soloAccountsToOpen, jointAccountsToOpen, jointAccountsToAccept]);

    // Map agreements to account types
    const agreementsByType = useMemo(() => {
        const typeMap: Record<string, (typeof agreementLinks)[0] | undefined> =
            {
                individualBrokerage: undefined,
                retirement: undefined,
                jointBrokerage: undefined,
            };

        agreementLinks.forEach((agreement) => {
            // Check which account type(s) have this agreement
            const hasIndividual = accountsByType.individualBrokerage.some(
                (acc) =>
                    acc.derived?.apexAgreements?.some(
                        (ag) => ag.agreementUri === agreement.id,
                    ),
            );
            const hasRetirement = accountsByType.retirement.some((acc) =>
                acc.derived?.apexAgreements?.some(
                    (ag) => ag.agreementUri === agreement.id,
                ),
            );
            const hasJoint = accountsByType.jointBrokerage.some((acc) =>
                acc.derived?.apexAgreements?.some(
                    (ag) => ag.agreementUri === agreement.id,
                ),
            );

            if (hasIndividual) {
                typeMap.individualBrokerage = agreement;
            }
            if (hasRetirement) {
                typeMap.retirement = agreement;
            }
            if (hasJoint) {
                typeMap.jointBrokerage = agreement;
            }
        });

        return typeMap;
    }, [agreementLinks, accountsByType]);

    // All hooks must be called before any conditional returns
    // Now we can check conditions and return early if needed

    if (
        isLoadingMoneyMap ||
        isLoadingCompleteness ||
        isLoadingInvestmentProfile
    ) {
        return <FullScreenSpinner />;
    }

    /* Redirect if apex is not enabled */
    if (!enableApexAccounts) {
        return <Redirect href="/roadmap/go/start" />;
    }

    /* Show Under Review when manual intervention is required, regardless of member actions */
    const isManualInterventionRequired =
        investmentProfile?.manualInterventionStatus ===
        ManualInterventionStatus.REQUIRED;
    if (isManualInterventionRequired) {
        return <ApexAccountsUnderReview />;
    }

    /**
     * When member has no actions to take: show Under Review if
     * not all accounts are in good standing (e.g. under review at Apex), otherwise Success.
     */
    const noActionsForMember = memberHasNoApexActionsLeft(
        currentApexAccountsStep,
    );
    if (noActionsForMember && !isLoadingCompleteness) {
        if (currentApexAccountsStep?.areAllAccountsInGoodStanding === true) {
            return (
                <ApexAccountsSuccess
                    isPendingInvestigationRun={
                        currentApexAccountsStep.isCompletionPendingInvestigationRun
                    }
                    isWaitingForPartner={isWaitingForPartner}
                    canProceedToIncomeSplit={canProceedToIncomeSplitStep}
                />
            );
        } else {
            return <ApexAccountsUnderReview />;
        }
    }

    if (moneyMap == null || hasMoneyMapError) {
        captureException(
            new Error('Tried to load ApexAccounts step with no Money Map'),
        );
        toast.show(
            `There was an error while loading your Money Map. Please reach out to support at ${SUPPORT_EMAIL}`,
        );
        return <Redirect href="/home/roadmap" />;
    }

    // Should not be possible to trigger this, but just in case.
    if (!isIncluded || !isEnabled) {
        captureException(
            new Error('Cannot load ApexAccounts step: invalid state'),
            {
                isIncluded,
                isEnabled,
                memberHasAccountsToOpen,
                memberHasAccountsToAccept,
            },
        );
        toast.show('There was an issue loading the Investment Accounts step.');
        return <Redirect href="/roadmap/go/start" />;
    }

    // Track view (not a hook, safe to call after conditionals)
    trackViewOnce('Roadmap/Go/InvestmentAccounts');

    // Helper to render account type section with validation
    const renderAccountTypeSection = (
        accounts: MoneyMapNewApexAccount[],
        agreement: (typeof agreementLinks)[0] | undefined,
        sectionName: string,
    ) => {
        if (accounts.length === 0) return null;

        if (!agreement) {
            captureException(
                new Error(`Missing agreement for ${sectionName} accounts`),
                {
                    accountCount: accounts.length,
                    accountIds: accounts.map((a) => a.id),
                },
            );
            return null;
        }

        return (
            <AccountTypeSection
                accounts={accounts}
                agreement={agreement}
                agreementCheckboxes={agreementCheckboxes}
                setAgreementCheckboxes={setAgreementCheckboxes}
            />
        );
    };

    return (
        <View height="100%">
            <GoWorkflowHeader
                stepType={GoWorkflowStepType.APEX_ACCOUNTS}
                title="Investment Accounts"
                hasMessagingButton={false}
            />
            <ScreenContainer useSafeAreaInsetTop={false}>
                <YStack marginTop={12} justifyContent="space-between">
                    <YStack width="100%" gap={24}>
                        {/* Individual Brokerage Section */}
                        {renderAccountTypeSection(
                            accountsByType.individualBrokerage,
                            agreementsByType.individualBrokerage,
                            'individual brokerage',
                        )}

                        {/* Retirement Accounts Section */}
                        {renderAccountTypeSection(
                            accountsByType.retirement,
                            agreementsByType.retirement,
                            'retirement',
                        )}

                        {/* Joint Brokerage Section */}
                        {renderAccountTypeSection(
                            accountsByType.jointBrokerage,
                            agreementsByType.jointBrokerage,
                            'joint brokerage',
                        )}
                    </YStack>

                    <YStack width="100%" gap={16}>
                        <BatchOpenApexAccountsBankingTerms
                            termsAccepted={acceptTerms}
                            onPress={() => {
                                setAcceptTerms((oldVal) => {
                                    return !oldVal;
                                });
                            }}
                            acknowledgementText={`By clicking "${buttonText}" you confirm you have reviewed and accept Fruitful Advisory LLC's`}
                            backgroundColor="$gray200"
                            padding={16}
                            borderRadius={16}
                        />
                        <Button
                            disabled={
                                isCreatingAccounts ||
                                !acceptTerms ||
                                agreementLinks.some(
                                    (agreement) =>
                                        !agreementCheckboxes[agreement.id],
                                )
                            }
                            text={isCreatingAccounts ? '' : buttonText}
                            size={ButtonSize.LARGE}
                            variant={ButtonVariant.PRIMARY}
                            icon={isCreatingAccounts ? <Spinner /> : undefined}
                            onPress={showBackgroundInfoSheet}
                        />
                        <YStack marginBottom={24} width="100%" gap={8}>
                            <LabelRegular color="$gray900">
                                Investment advice offered by Fruitful Advisory,
                                LLC, a registered investment adviser. Additional
                                disclosures can be referenced at{' '}
                                <Link
                                    href="https://www.fruitful.com/legal"
                                    target="_blank"
                                >
                                    <LabelRegular
                                        textDecorationLine="underline"
                                        color="$gray900"
                                    >
                                        fruitful.com/legal
                                    </LabelRegular>
                                </Link>
                                .
                            </LabelRegular>
                        </YStack>
                    </YStack>
                </YStack>
            </ScreenContainer>

            {showBackgroundSheet && (
                <ApexComplianceFlow
                    isOpen={true}
                    onClose={() => {
                        setShowBackgroundSheet(false);
                    }}
                    onComplete={handleComplianceComplete}
                />
            )}
        </View>
    );
};

// Helper to determine section header
const getAccountTypeSectionHeader = (
    accounts: MoneyMapNewApexAccount[],
): string => {
    if (accounts.length === 0) return '';

    const firstAccountType = accounts[0]?.createParameters?.apexAccountType;
    if (firstAccountType == null) return '';

    const sectionType = apexAccountTypeToSection[firstAccountType];
    const isPlural = accounts.length > 1;

    return isPlural
        ? sectionHeaders[sectionType].plural
        : sectionHeaders[sectionType].singular;
};

// Component for rendering individual account rows
const ApexAccountRow = ({ account }: { account: MoneyMapNewApexAccount }) => {
    const { name, apexAccountType } = account.createParameters ?? {};

    return (
        <XStack gap={12} alignItems="center">
            <FruitfulCustomIcon
                size="sm"
                icon={account.createParameters?.icon ?? FRUITFUL_ICON_KEY}
                color={
                    account.createParameters?.color ??
                    MoneyMapCategoryToAccountColorMap[MoneyMapCategory.GOALS]
                }
                useAltColors={true}
            />
            <YStack>
                <BodyMediumRegular>{name}</BodyMediumRegular>
                {apexAccountType != null && (
                    <LabelRegular color="$gray900">
                        {ReadableApexAccountRegistrationType[apexAccountType]}
                    </LabelRegular>
                )}
            </YStack>
        </XStack>
    );
};

// Section component for each account type. Each section has 1 checkbox for the agreement unique to that section.
const AccountTypeSection = ({
    accounts,
    agreement,
    agreementCheckboxes,
    setAgreementCheckboxes,
}: {
    accounts: MoneyMapNewApexAccount[];
    agreement: {
        id: string;
        linkText: string;
        linkUrl: Href;
        text: string;
    };
    agreementCheckboxes: Record<string, boolean>;
    setAgreementCheckboxes: React.Dispatch<
        React.SetStateAction<Record<string, boolean>>
    >;
}) => {
    const header = getAccountTypeSectionHeader(accounts);

    return (
        <YStack gap={16} marginBottom={8}>
            <BodySmallEmphasized>{header}</BodySmallEmphasized>
            <YStack gap={16}>
                {accounts.map((account) => (
                    <ApexAccountRow key={account.id} account={account} />
                ))}
            </YStack>
            <ApexAgreementCheckbox
                id={agreement.id}
                isChecked={agreementCheckboxes[agreement.id] ?? false}
                onPress={() => {
                    setAgreementCheckboxes((oldVal) => ({
                        ...oldVal,
                        [agreement.id]: !oldVal[agreement.id],
                    }));
                }}
                linkText={agreement.linkText}
                linkUrl={agreement.linkUrl}
                text={agreement.text}
                backgroundColor="$gray200"
                padding={16}
                borderRadius={16}
            />
        </YStack>
    );
};
